import type { Edge, FlowLayout } from './types';

// Assign horizontal anchor slots (0..1) for each edge on each node, so
// multiple outgoing/incoming edges at the same node fan out across the
// card's bottom/top instead of all forking from the same centre point.
//
// Key rules:
// - Single edge → centre (0.5).
// - Back-edges (target above source) always exit on the LEFT — they route
//   via a left-hand back-lane so their exit position shouldn't depend on
//   where the target sits in x-space after a layout remap.
// - Destination-bound edges (terminal "→ aXcelerate" arrows) always claim
//   the centre slot on the source card. Side edges distribute around
//   them. This keeps the "main downward path" dead-centre even when a
//   card also emits an escalation edge to a lateral handler.
// - When one of the outgoing edges targets a centre-aligned node (by x
//   position, not by destination kind), it claims 0.5 and the rest
//   distribute in their respective halves.
// - Otherwise, spread evenly across the whole bottom (or top).
// - After slot assignment, adjacent slots must be at least MIN_SLOT_GAP
//   apart. If they're closer (e.g. two targets happen to sit at similar
//   x positions), the crowded slots get pushed apart within the
//   available 0..1 range.
//
// Edges are sorted by the opposite endpoint's x so the left-to-right slot
// order matches the left-to-right target order. This keeps lines from
// crossing each other.
const STRAIGHT_TOLERANCE = 8; // px
const MIN_SLOT_GAP = 0.18;

export function assignSlots(
  container: HTMLElement,
  layout: FlowLayout,
  groupMap: Map<string, Edge[]>,
  side: 'out' | 'in',
) {
  const centerX = (id: string) => {
    const el = container.querySelector<HTMLElement>(
      `[data-flow-id="${id}"]`,
    );
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return r.left + r.width / 2;
  };

  // Sort siblings by opposite endpoint's x so slots stay ordered
  // left-to-right and lines don't cross each other.
  for (const group of groupMap.values()) {
    group.sort((a, b) =>
      side === 'out'
        ? centerX(a.to) - centerX(b.to)
        : centerX(a.from) - centerX(b.from),
    );
  }

  // Find the destination node id (the terminal "→ aXcelerate" node)
  // so we can pin any edge that targets it to slot 0.5 on the source.
  // Only one destination kind node exists per chart; this lookup is
  // cheap enough to do per-call.
  const destinationId = layout.nodes.find((n) => n.kind === 'destination')?.id;

  const slots = new Map<Edge, number>();
  for (const [nodeId, edges] of groupMap) {
    if (edges.length === 1) {
      slots.set(edges[0]!, 0.5);
      continue;
    }

    // Early single-forward-edge centering: if the node has any number
    // of back-edges plus exactly ONE forward edge, the forward edge
    // should still exit dead-centre (0.5). Back-edges are pinned left
    // (0.15+) so they don't collide with centre. This keeps the
    // "single-line → centre of card" rule intact even when a back-edge
    // (e.g. escalation-resolved loop) exists on the same node.

    const selfEl = container.querySelector<HTMLElement>(
      `[data-flow-id="${nodeId}"]`,
    );
    const selfRect = selfEl?.getBoundingClientRect();
    const isBackEdgeFrom = (edge: Edge) => {
      if (side !== 'out' || !selfRect) return false;
      const tEl = container.querySelector<HTMLElement>(
        `[data-flow-id="${edge.to}"]`,
      );
      if (!tEl) return false;
      return tEl.getBoundingClientRect().top < selfRect.bottom;
    };
    const backEdges: Edge[] = [];
    const forwardEdges: Edge[] = [];
    for (const e of edges) {
      if (isBackEdgeFrom(e)) backEdges.push(e);
      else forwardEdges.push(e);
    }
    backEdges.forEach((e, i) => {
      slots.set(e, 0.15 + i * 0.07);
    });

    // Single forward edge coexisting with back-edges → still centre it.
    if (forwardEdges.length === 1) {
      slots.set(forwardEdges[0]!, 0.5);
      continue;
    }

    // Destination-pinning rule: when one of the outgoing forward edges
    // targets the chart's destination node (the terminal "→ aXcelerate"
    // arrow on the SOURCE side only — not on the destination's incoming
    // side), it claims 0.5 and the remaining forwards distribute
    // around it in their respective halves based on x direction. This
    // guarantees the "main downward path" exits dead-centre even when
    // a card also emits an escalation edge to a lateral handler.
    if (side === 'out' && destinationId) {
      const destEdge = forwardEdges.find((e) => e.to === destinationId);
      if (destEdge && forwardEdges.length > 1) {
        slots.set(destEdge, 0.5);
        const others = forwardEdges.filter((e) => e !== destEdge);
        const leftSide: Edge[] = [];
        const rightSide: Edge[] = [];
        const selfC = centerX(nodeId);
        for (const e of others) {
          const tC = centerX(e.to);
          if (tC < selfC) leftSide.push(e);
          else rightSide.push(e);
        }
        const placeSide = (list: Edge[], minFrac: number, maxFrac: number) => {
          if (list.length === 0) return;
          if (list.length === 1) {
            slots.set(list[0]!, (minFrac + maxFrac) / 2);
            return;
          }
          list.forEach((e, i) => {
            const f =
              minFrac + ((i + 1) / (list.length + 1)) * (maxFrac - minFrac);
            slots.set(e, f);
          });
        };
        placeSide(leftSide, 0.08, 0.4);
        placeSide(rightSide, 0.6, 0.92);
        continue;
      }
    }

    const selfCentre = centerX(nodeId);
    type Tagged = { edge: Edge; dir: 'left' | 'centre' | 'right' };
    const tagged: Tagged[] = forwardEdges.map((edge) => {
      const otherId = side === 'out' ? edge.to : edge.from;
      const otherCentre = centerX(otherId);
      const diff = otherCentre - selfCentre;
      const dir =
        Math.abs(diff) <= STRAIGHT_TOLERANCE
          ? 'centre'
          : diff < 0
            ? 'left'
            : 'right';
      return { edge, dir };
    });

    if (tagged.length === 0) continue;

    const centreEdges = tagged.filter((t) => t.dir === 'centre');

    if (centreEdges.length === 0) {
      const rangeMin = backEdges.length > 0 ? 0.45 : 0.1;
      const rangeMax = 0.9;
      const count = forwardEdges.length;
      forwardEdges.forEach((edge, i) => {
        const f =
          rangeMin + ((i + 1) / (count + 1)) * (rangeMax - rangeMin);
        slots.set(edge, f);
      });
    } else {
      const leftEdges = tagged.filter((t) => t.dir === 'left');
      const rightEdges = tagged.filter((t) => t.dir === 'right');
      centreEdges.forEach((t, i) => {
        if (i === 0) {
          slots.set(t.edge, 0.5);
        } else {
          const offset = 0.06 * Math.ceil(i / 2) * (i % 2 === 0 ? 1 : -1);
          slots.set(t.edge, 0.5 + offset);
        }
      });

      const placeSide = (list: Tagged[], minFrac: number, maxFrac: number) => {
        if (list.length === 0) return;
        if (list.length === 1) {
          slots.set(list[0]!.edge, (minFrac + maxFrac) / 2);
          return;
        }
        list.forEach((t, i) => {
          const f =
            minFrac + ((i + 1) / (list.length + 1)) * (maxFrac - minFrac);
          slots.set(t.edge, f);
        });
      };
      placeSide(leftEdges, 0.08, 0.42);
      placeSide(rightEdges, 0.58, 0.92);
    }
  }

  // Enforce MIN_SLOT_GAP between adjacent slots on the same node. Two
  // edges landing < MIN_SLOT_GAP apart render as visually-overlapping
  // arrow heads; push them apart within the available range. Runs per
  // node so we don't bleed gaps across unrelated groups.
  for (const edges of groupMap.values()) {
    if (edges.length < 2) continue;
    const sorted = edges
      .map((e) => ({ edge: e, slot: slots.get(e) ?? 0.5 }))
      .sort((a, b) => a.slot - b.slot);
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1]!;
      const curr = sorted[i]!;
      if (curr.slot - prev.slot < MIN_SLOT_GAP) {
        // Push curr rightward — but clamp to 0.98 and, if we hit the
        // ceiling, push prev leftward instead.
        const desired = prev.slot + MIN_SLOT_GAP;
        if (desired <= 0.98) {
          curr.slot = desired;
        } else {
          curr.slot = 0.98;
          prev.slot = Math.max(0.02, curr.slot - MIN_SLOT_GAP);
        }
        slots.set(curr.edge, curr.slot);
        slots.set(prev.edge, prev.slot);
      }
    }
  }

  return slots;
}

// Group edges by source and target — helper used before assignSlots so the
// caller has Map<sourceId, Edge[]> for outgoing and Map<targetId, Edge[]>
// for incoming.
export function groupEdges(edges: readonly Edge[]) {
  const outgoing = new Map<string, Edge[]>();
  const incoming = new Map<string, Edge[]>();
  for (const edge of edges) {
    if (!outgoing.has(edge.from)) outgoing.set(edge.from, []);
    outgoing.get(edge.from)!.push(edge);
    if (!incoming.has(edge.to)) incoming.set(edge.to, []);
    incoming.get(edge.to)!.push(edge);
  }
  return { outgoing, incoming };
}
